import { createClient } from '@/lib/supabase/client';

export interface SitePage {
  slug: string;
  title: string;
  subtitle: string | null;
  version: string | null;
  effectiveDate: string | null;
  lastReviewed: string | null;
  nextReview: string | null;
  bodyMarkdown: string;
}

export interface FooterLink {
  slug: string;
  label: string;
  order: number;
}

export const LEGAL_SLUGS = ['privacy-policy', 'cookie-policy', 'terms-and-conditions', 'accessibility'] as const;

export const FALLBACK_FOOTER_LINKS: FooterLink[] = [
  { slug: 'privacy-policy', label: 'Privacy Policy', order: 1 },
  { slug: 'cookie-policy', label: 'Cookie Policy', order: 2 },
  { slug: 'terms-and-conditions', label: 'Terms and Conditions', order: 3 },
  { slug: 'accessibility', label: 'Accessibility', order: 4 },
];

let cachedFooterLinks: FooterLink[] | null = null;
let pendingFooterLinks: Promise<FooterLink[]> | null = null;

export function getCachedFooterLinks(): FooterLink[] | null {
  return cachedFooterLinks;
}

export const sitePagesService = {
  async getBySlug(slug: string): Promise<SitePage | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('site_pages')
      .select('slug, title, subtitle, version, effective_date, last_reviewed, next_review, body_markdown')
      .eq('slug', slug)
      .eq('published', true)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      slug: data.slug,
      title: data.title ?? '',
      subtitle: data.subtitle ?? null,
      version: data.version ?? null,
      effectiveDate: data.effective_date ?? null,
      lastReviewed: data.last_reviewed ?? null,
      nextReview: data.next_review ?? null,
      bodyMarkdown: data.body_markdown ?? '',
    };
  },

  async getFooterLinks(): Promise<FooterLink[]> {
    if (cachedFooterLinks) return cachedFooterLinks;
    if (pendingFooterLinks) return pendingFooterLinks;
    pendingFooterLinks = (async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('site_pages')
        .select('slug, footer_label, footer_order')
        .eq('published', true)
        .not('footer_label', 'is', null)
        .order('footer_order', { ascending: true });
      if (error) throw error;
      const links = (data ?? [])
        .filter((r: any) => (r.footer_label ?? '').trim() !== '')
        .map((r: any) => ({ slug: r.slug as string, label: r.footer_label as string, order: (r.footer_order ?? 0) as number }));
      cachedFooterLinks = links;
      return links;
    })().finally(() => {
      pendingFooterLinks = null;
    });
    return pendingFooterLinks;
  },
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function formatLongDate(value: string | null | undefined): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const day = parseInt(m[3], 10);
  const month = MONTHS[parseInt(m[2], 10) - 1];
  if (!month) return value;
  return `${day} ${month} ${m[1]}`;
}
